// ─────────────────────────────────────────────
//  Onboarding wizard — step order, resume point, and validation.
//
//  Pure: no React, no Firestore. The page renders whatever nextStepKey says,
//  which is what makes the wizard resumable after the app is closed mid-way.
// ─────────────────────────────────────────────
export const WIZARD_STEPS = [
  // ⚠️ THE PERSON'S NAME COMES FIRST, and it is here because of what the board
  // does with it. A self-signed-up account has no Auth displayName, so
  // buildDefaultProfile falls back to the EMAIL LOCAL-PART — and that string is
  // then printed on the board as a clinician: "☕ Giving a break: dummy.md1"
  // (seen in the sandbox, 2026-08-18), and the same on the BREAKS card, the
  // GIVING BREAKS chip and the mobile break board. The wizard asked for the
  // organization, the building, the room count and the head count, and never
  // once for the name of the person standing in the room.
  { key: "you",          title: "What is your name?" },
  { key: "org",          title: "What is your organization called?" },
  { key: "facilityType", title: "What kind of facility is it?" },
  { key: "rooms",        title: "How many operating rooms?" },
  { key: "team",         title: "How big is the team?" },
];

export const FACILITY_TYPES = [
  "Hospital OR",
  "Ambulatory surgery center",
  "Office-based practice",
  "Other",
];

export const MAX_ORS = 60;
export const MAX_TEAM = 500;

const ANSWER_KEY = { you: "yourName", org: "orgName", facilityType: "facilityType", rooms: "orCount", team: "teamSize" };

// A number answer of 0 is ANSWERED, not missing — Number(null) === 0 has bitten
// this codebase before, so absence is tested explicitly rather than by falsiness.
const isAnswered = (v) =>
  typeof v === "number" ? Number.isFinite(v) : typeof v === "string" ? v.trim().length > 0 : false;

export function nextStepKey(answers = {}) {
  const step = WIZARD_STEPS.find((s) => !isAnswered(answers[ANSWER_KEY[s.key]]));
  return step ? step.key : null;
}

export function validateStep(key, value) {
  switch (key) {
    case "you":
      // A name is what a colleague reads on a room card at arm's length. One
      // character is not that, and the board has no better fallback to reach
      // for — the alternative it used was an email fragment.
      return typeof value === "string" && value.trim().length >= 2
        ? null : "Enter the name your colleagues will see.";
    case "org":
      return typeof value === "string" && value.trim().length > 0
        ? null : "Enter your organization's name.";
    case "facilityType":
      return FACILITY_TYPES.includes(value) ? null : "Choose a facility type.";
    case "rooms":
      if (!Number.isInteger(value)) return "Enter a whole number of rooms.";
      if (value < 1) return "Enter at least 1 room.";
      if (value > MAX_ORS) return `That is more than ${MAX_ORS} rooms — contact us for a facility this size.`;
      return null;
    case "team":
      if (!Number.isInteger(value)) return "Enter a whole number of people.";
      if (value < 1) return "Enter at least 1 person.";
      if (value > MAX_TEAM) return `That is more than ${MAX_TEAM} people — contact us for a team this size.`;
      return null;
    default:
      return "Unknown step.";
  }
}

export const answerFieldFor = (key) => ANSWER_KEY[key];
